import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuthorizationParams } from '@client/hooks/useAuthorizationParams';
import { useAuth0Redirect } from '@client/hooks/useAuth0Redirect';
import { authApi } from '@client/api/waterlily';
import { getDomainFromEmail, formatPhoneNumber, getBaseUrl } from '@shared';
import { analytics } from '@client/analytics';

const isLocalDev = () => getBaseUrl().includes('localhost');

const phoneNumberSchema = z
  .string()
  .min(1, 'Phone number is required')
  .refine(
    val => {
      const digits = val.replace(/\D/g, '');
      // Accept 10 digits, or 11 digits starting with 1 (US country code)
      if (digits.length === 10) {
        return true;
      }
      if (digits.length === 11 && digits.startsWith('1')) {
        return true;
      }
      return false;
    },
    { message: 'Please enter a valid 10-digit US phone number' },
  )
  .refine(
    val => {
      const digits = val.replace(/\D/g, '');
      // Get the area code (first 3 digits, or digits 2-4 if starts with country code 1)
      const areaCode = digits.length === 11 ? digits.slice(1, 4) : digits.slice(0, 3);
      // US area codes cannot start with 0 or 1
      return !areaCode.startsWith('0') && !areaCode.startsWith('1');
    },
    { message: 'Please enter a valid US area code' },
  );

const emailOnlySchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .toLowerCase()
    .email('Please enter a valid email address'),
  phoneNumber: z.string().optional().default(''),
});

export const signupFormSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .toLowerCase()
    .email('Please enter a valid email address'),
  phoneNumber: phoneNumberSchema,
});

export type SignupFormValues = z.infer<typeof signupFormSchema>;

export function useSignupForm() {
  const [searchParams] = useSearchParams();
  const emailHint = searchParams.get('email_hint');
  const overrideConnection = searchParams.get('override_connection');
  const isPasswordMode = overrideConnection === 'password' && isLocalDev();
  const [existingAdvisor, setExistingAdvisor] = useState<AdvisorExistsCheckResponse | null>(null);

  const { loginWithRedirect, isRedirecting } = useAuth0Redirect();
  const {
    authorizationParams,
    searchParams: { redirectTo },
  } = useAuthorizationParams();

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(isPasswordMode ? emailOnlySchema : signupFormSchema),
    mode: 'onChange',
    defaultValues: {
      email: emailHint ?? '',
      phoneNumber: '',
    },
  });

  const email = form.watch('email');
  const phoneNumber = form.watch('phoneNumber');

  useEffect(() => {
    if (existingAdvisor) {
      setExistingAdvisor(null);
    }
  }, [email, phoneNumber]);

  const advisorExistsMutation = useMutation({
    mutationFn: authApi.postAdvisorExistsForPublic,
    onSuccess: (data, variables) => {
      if (data.exists) {
        setExistingAdvisor(data);
      } else {
        proceedWithSignup(variables.email, variables.phoneNumber);
      }
    },
  });

  const proceedWithSignup = (email: string, phoneNumber: string) => {
    if (isPasswordMode) {
      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          'connection': 'Username-Password-Authentication',
          'login_hint': email,
        },
        appState: {
          returnTo: redirectTo || '/',
        },
      });
      return;
    }

    const isInternalUser = getDomainFromEmail(email) === 'joinwaterlily.com';
    const shouldUseEmail = isInternalUser || overrideConnection === 'email';
    const signupConnection = shouldUseEmail ? 'email' : 'sms';

    loginWithRedirect({
      authorizationParams: {
        ...authorizationParams,
        'connection': signupConnection,
        'login_hint': signupConnection === 'sms' ? phoneNumber : email,
        'ext-email': signupConnection === 'sms' ? email : undefined,
      },
      appState: {
        returnTo: redirectTo || '/',
      },
    });
  };

  const onSubmit = (data: SignupFormValues) => {
    analytics.formSubmit('signup_form', {
      email: data.email,
      phoneNumber: data.phoneNumber,
    });
    setExistingAdvisor(null);
    advisorExistsMutation.mutate({
      email: data.email,
      phoneNumber: data.phoneNumber,
    });
  };

  const handleSocialLogin = (
    socialConnection: Extract<Auth0Connection, 'google-oauth2' | 'windowslive'>,
  ) => {
    analytics.track('signup_social_login_click', {
      provider: socialConnection === 'google-oauth2' ? 'google' : 'microsoft',
    }, 'action');
    loginWithRedirect({
      authorizationParams: {
        ...authorizationParams,
        'connection': socialConnection,
      },
      appState: {
        returnTo: redirectTo || '/',
      },
    });
  };

  const clearExistingAdvisorError = () => {
    setExistingAdvisor(null);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    form.setValue('phoneNumber', formatted, { shouldValidate: true });
  };

  return {
    form,
    onSubmit,
    handleSocialLogin,
    handlePhoneNumberChange,
    isChecking: advisorExistsMutation.isPending,
    isRedirecting,
    checkError: advisorExistsMutation.error,
    existingAdvisor,
    clearExistingAdvisorError,
    isPasswordMode,
  };
}
